import axiosInstance from '@/services/axiosInstance';
import { pickBody } from '@/api/helpers';

export type OrgHoliday = {
  id: string;
  organizationId: string;
  name: string;
  startsOn: string;
  endsOn: string | null;
  yearlyRepeat: boolean;
  description: string | null;
  reminderOffsetsMinutes?: number[] | null;
  createdById: string;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OrgWideEvent = {
  id: string;
  organizationId: string;
  title: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  venue: string | null;
  reminderOffsetsMinutes?: number[] | null;
  createdById: string;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OrgScheduleSettings = {
  id?: string;
  organizationId: string;
  timeZone: string | null;
  weeklyHours: Record<string, unknown> | null;
  updatedById: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type CreateOrgHolidayInput = {
  name: string;
  startsOn: string;
  endsOn?: string | null;
  yearlyRepeat?: boolean;
  description?: string | null;
  reminderOffsetsMinutes?: number[];
};

export type PatchOrgHolidayInput = Partial<CreateOrgHolidayInput>;

export type CreateOrgWideEventInput = {
  title: string;
  description?: string | null;
  startsAt: string;
  endsAt: string;
  allDay?: boolean;
  venue?: string | null;
  reminderOffsetsMinutes?: number[];
};

export type PatchOrgWideEventInput = Partial<CreateOrgWideEventInput>;

const listHolidays = (): Promise<OrgHoliday[]> =>
  axiosInstance.get<unknown>('/api/v1/org/holidays').then((res) => {
    const raw = pickBody<{ success?: boolean; data: OrgHoliday[] } | OrgHoliday[]>(res.data);
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  });

const listWideEvents = (params?: { from?: string; to?: string }): Promise<OrgWideEvent[]> =>
  axiosInstance.get<unknown>('/api/v1/org/wide-events', { params }).then((res) => {
    const raw = pickBody<{ success?: boolean; data: OrgWideEvent[] } | OrgWideEvent[]>(res.data);
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  });

const getSchedule = (): Promise<OrgScheduleSettings> =>
  axiosInstance.get<unknown>('/api/v1/org/schedule').then((res) => {
    const raw = pickBody<{ success?: boolean; data: OrgScheduleSettings }>(res.data);
    return raw.data;
  });

const createHoliday = (input: CreateOrgHolidayInput): Promise<OrgHoliday> =>
  axiosInstance.post<unknown>('/api/v1/org/holidays', input).then((res) => {
    const raw = pickBody<{ success?: boolean; data: OrgHoliday }>(res.data);
    return raw.data;
  });

const patchHoliday = (id: string, input: PatchOrgHolidayInput): Promise<OrgHoliday> =>
  axiosInstance.patch<unknown>(`/api/v1/org/holidays/${id}`, input).then((res) => {
    const raw = pickBody<{ success?: boolean; data: OrgHoliday }>(res.data);
    return raw.data;
  });

const deleteHoliday = (id: string): Promise<void> =>
  axiosInstance.delete(`/api/v1/org/holidays/${id}`).then(() => undefined);

const createWideEvent = (input: CreateOrgWideEventInput): Promise<OrgWideEvent> =>
  axiosInstance.post<unknown>('/api/v1/org/wide-events', input).then((res) => {
    const raw = pickBody<{ success?: boolean; data: OrgWideEvent }>(res.data);
    return raw.data;
  });

const patchWideEvent = (id: string, input: PatchOrgWideEventInput): Promise<OrgWideEvent> =>
  axiosInstance.patch<unknown>(`/api/v1/org/wide-events/${id}`, input).then((res) => {
    const raw = pickBody<{ success?: boolean; data: OrgWideEvent }>(res.data);
    return raw.data;
  });

const deleteWideEvent = (id: string): Promise<void> =>
  axiosInstance.delete(`/api/v1/org/wide-events/${id}`).then(() => undefined);

const orgApi = {
  listHolidays,
  listWideEvents,
  getSchedule,
  createHoliday,
  patchHoliday,
  deleteHoliday,
  createWideEvent,
  patchWideEvent,
  deleteWideEvent,
};

export { orgApi };
